import { inject, Injectable } from '@angular/core';

import { first, Observable } from 'rxjs';
import { Comment } from '@core/models/comment.model';
import { Attachment } from '@core/models';
import { CommentLoaderKey } from '@core/enums/comment.enum';
import { CommentableResourceService } from '@core/interfaces/commentable-resource.interface';
import { HttpParams } from '@angular/common/http';

import {
  CreateShipmentPayload,
  Shipment,
  ShipmentDetail,
  ShipmentExceptions,
  ShipmentFilters,
  ShipmentKpis,
  ShipmentOrderOption,
  UpdateShipmentPayload,
} from '@core/models/shipment.model';
import { PaginatedResponse } from '@core/models/response';
import { ShipmentLoaderKey } from '@core/enums';
import { ApiService } from '@core/services/api.service';
import { LoadingService } from './loading';
import { SnackbarService } from './snackbar.service';
import { cleanHTTPParams } from '@core/utils/helpers';

const ENDPOINT = 'v1/shipments';

@Injectable({
  providedIn: 'root',
})
export class ShipmentService implements CommentableResourceService {
  private readonly api = inject(ApiService);
  private readonly loader = inject(LoadingService);
  private readonly snack = inject(SnackbarService);

  list(filters: ShipmentFilters): Observable<PaginatedResponse<Shipment>> {
    const params = new HttpParams({ fromObject: cleanHTTPParams(filters) });
    return this.api
      .get<PaginatedResponse<Shipment>>(ENDPOINT, { params })
      .pipe(first(), this.loader.set(ShipmentLoaderKey.LIST_SHIPMENTS), this.snack.operator());
  }

  getKpis(): Observable<ShipmentKpis> {
    return this.api
      .get<ShipmentKpis>(`${ENDPOINT}/kpis`)
      .pipe(first(), this.loader.set(ShipmentLoaderKey.GET_KPIS), this.snack.operator());
  }

  getExceptions(): Observable<ShipmentExceptions> {
    return this.api
      .get<ShipmentExceptions>(`${ENDPOINT}/exceptions`)
      .pipe(first(), this.loader.set(ShipmentLoaderKey.GET_EXCEPTIONS), this.snack.operator());
  }

  get(id: string): Observable<ShipmentDetail> {
    return this.api
      .get<ShipmentDetail>(`${ENDPOINT}/${id}`)
      .pipe(first(), this.loader.set(ShipmentLoaderKey.GET_SHIPMENT), this.snack.operator());
  }

  create(payload: CreateShipmentPayload): Observable<Shipment> {
    return this.api
      .post<Shipment, CreateShipmentPayload>(ENDPOINT, payload)
      .pipe(first(), this.loader.set(ShipmentLoaderKey.CREATE_SHIPMENT), this.snack.operator('Shipment created'));
  }

  update(id: string, payload: UpdateShipmentPayload): Observable<Shipment> {
    return this.api
      .patch<Shipment, UpdateShipmentPayload>(`${ENDPOINT}/${id}`, payload)
      .pipe(first(), this.snack.operator('Shipment updated'));
  }

  linkOrder(shipmentId: string, orderId: string, link: boolean): Observable<void> {
    return this.api
      .post<void, { orderId: string; link: boolean }>(`${ENDPOINT}/${shipmentId}/link-order`, { orderId, link })
      .pipe(first());
  }

  listOrderOptions(params: { senderId?: string; receiverId?: string }): Observable<ShipmentOrderOption[]> {
    const httpParams = new HttpParams({ fromObject: cleanHTTPParams(params) });
    return this.api
      .get<ShipmentOrderOption[]>(`${ENDPOINT}/orders/select`, { params: httpParams })
      .pipe(first());
  }

  listComments(resourceId: string): Observable<Comment[]> {
    return this.api
      .get<Comment[]>(`${ENDPOINT}/${resourceId}/comments`)
      .pipe(first(), this.loader.set(CommentLoaderKey.LIST_COMMENTS), this.snack.operator());
  }

  createComment(resourceId: string, formData: FormData): Observable<Comment> {
    return this.api
      .post<Comment, FormData>(`${ENDPOINT}/${resourceId}/comments`, formData)
      .pipe(first(), this.loader.set(CommentLoaderKey.CREATE_COMMENT), this.snack.operator());
  }

  listAttachments(resourceId: string): Observable<Attachment[]> {
    return this.api
      .get<Attachment[]>(`${ENDPOINT}/${resourceId}/attachments`)
      .pipe(first(), this.loader.set(CommentLoaderKey.LIST_COMMENTS), this.snack.operator());
  }

  createAttachment(resourceId: string, file: File): Observable<Attachment> {
    const formData: FormData = new FormData();
    formData.set('file', file);
    return this.api
      .post<Attachment, FormData>(`${ENDPOINT}/${resourceId}/attachments`, formData)
      .pipe(first(), this.loader.set(CommentLoaderKey.CREATE_COMMENT), this.snack.operator());
  }
}
